import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Define variants for the spinner
const spinnerVariants = cva(
  "animate-spin rounded-full border-t-transparent", 
  {
    variants: {
      variant: {
        default: "border-vibebops-accent",
        primary: "border-primary",
        secondary: "border-secondary",
        success: "border-green-500",
        destructive: "border-destructive",
      },
      size: {
        sm: "h-4 w-4 border-2",
        md: "h-8 w-8 border-4",
        lg: "h-12 w-12 border-4",
        xl: "h-16 w-16 border-[6px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface LoadingSpinnerProps 
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  fullScreen?: boolean;
  text?: string;
}

const LoadingSpinner = ({
  className,
  variant,
  size,
  fullScreen = false,
  text,
  ...props
}: LoadingSpinnerProps) => {
  // Base spinner element
  const spinnerElement = (
    <div
      className={cn(spinnerVariants({ variant, size }), className)}
      {...props}
    />
  );
  
  // If text is provided, add it to a container with the spinner
  const spinnerWithText = text ? (
    <div className="flex flex-col items-center gap-3">
      {spinnerElement}
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  ) : spinnerElement;
  
  // If fullScreen is true, center in the viewport
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
        {spinnerWithText}
      </div>
    );
  }
  
  return spinnerWithText;
};

export { LoadingSpinner, spinnerVariants }; 